import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  untracked,
  viewChild,
  input,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AlertController, ModalController, Platform } from '@ionic/angular/standalone';
import { TranslocoService } from '@jsverse/transloco';
import { filter, from, of, startWith, Subject, switchMap, take, takeUntil } from 'rxjs';
import { DuelTierDataWithMeta } from 'src/app/core/services/tiers/tiers.service';
import { WaitingOpponentsData } from 'src/app/core/api/player/schemas/waiting-opponents';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { TiersService } from 'src/app/core/services/tiers/tiers.service';
import { SoloTiersService } from 'src/app/core/services/tiers/solo-tiers.service';
import { WaitingOpponentsService } from 'src/app/core/services/waiting-opponents/waiting-opponents.service';
import { GameId } from 'src/app/shared/models/game/game-id';
import { ContinueGameDrawerComponent } from 'src/app/features/game/continue-game-drawer/continue-game-drawer.component';
import { WagerDrawerComponent } from 'src/app/features/wager/wager-drawer/wager-drawer.component';
import { GameControlDisplay } from '../../services/game-ui/game-control-display';
import {
  HintMessageFetcher,
  NotificationHintComponent,
} from 'src/app/ui/feedback/notification-hint/notification-hint.component';
import { ActivityFeedService } from 'src/app/core/services/activity-feed/activity-feed.service';
import { TournamentService } from 'src/app/core/services/tournament/tournament.service';
import { TournamentDataModel } from 'src/app/core/services/tournament/models/tournament-data-model';
import { ButtonComponent } from '../../../../ui/buttons/button/button.component';
import { CountdownPillComponent } from '../../../../ui/pills/countdown-pill/countdown-pill.component';
import { createUnfinishedCountdown } from 'src/app/core/services/rooms-results/unfinished-countdown';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import {
  getModalConfig,
  modalContent,
  shouldShowAsSheetModal,
} from 'src/app/core/services/modal/modal.utils';
import { GameTutorialService } from 'src/app/features/tutorials/services/game-tutorial.service';
import { SoloSheetService } from 'src/app/features/solo/services/solo-sheet.service';
import { RoomsResultsService } from 'src/app/core/services/rooms-results/rooms-results.service';
import { TierPlayEventData } from 'src/app/core/services/tiers/models/payment-events';
import { StorageService } from 'src/app/core/services/storage/storage.service';

@Component({
  selector: 'app-loading-game-controls',
  templateUrl: './loading-game-controls.component.html',
  styleUrls: ['./loading-game-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CountdownPillComponent, NotificationHintComponent, ButtonComponent],
})
export class LoadingGameControlsComponent
  implements OnInit, OnDestroy, AfterViewInit, GameControlDisplay
{
  // The hint sits in an @else branch, so each time a new instance renders it is started once.
  private readonly hintMessage = viewChild<NotificationHintComponent>('hintMessage');
  readonly gameId = input.required<GameId>();
  readonly autoOpenWagerDrawer = input(false);
  readonly autoOpenSoloSheet = input(false);

  readonly showPaymentEvent = output<TierPlayEventData>();
  readonly launchPracticeEvent = output<void>();
  readonly launchLocalEvent = output<void>();

  private translocoService = inject(TranslocoService);
  private platform = inject(Platform);
  private modalController = inject(ModalController);
  private operatorService = inject(OperatorService);
  private tiersService = inject(TiersService);
  private soloTiersService = inject(SoloTiersService);
  private soloSheetService = inject(SoloSheetService);
  private waitingOpponentsService = inject(WaitingOpponentsService);
  private analyticsService = inject(AnalyticsService);
  private roomsResultsService = inject(RoomsResultsService);
  private activityFeedService = inject(ActivityFeedService);
  private tournamentService = inject(TournamentService);
  private themeService = inject(ThemeService);
  private gameTutorialService = inject(GameTutorialService);
  private storageService = inject(StorageService);
  private alertController = inject(AlertController);
  private destroyRef = inject(DestroyRef);

  private destroy$ = new Subject<void>();
  private readonly allTiers = computed(() => this.tiersService.duelTiers() ?? []);
  private readonly freeMatches = this.tiersService.freeMatches;
  private currentTournamentData = signal<TournamentDataModel | undefined>(undefined);

  private hasControls = signal(true);
  readonly showingControls: Signal<boolean> = this.hasControls.asReadonly();

  waitingOpponentsStandard = signal<WaitingOpponentsData | undefined>(undefined);

  readonly hasUnfinishedRoom = this.roomsResultsService.hasUnfinishedRoom;

  private initialised$ = toObservable(this.roomsResultsService.initialised);

  readonly unfinishedTimeRemaining = createUnfinishedCountdown(
    this.roomsResultsService,
    this.translocoService,
  );

  readonly playButtonLabel = computed(() =>
    this.hasUnfinishedRoom()
      ? this.getTranslation('continue-game')
      : this.getTranslation('play-now'),
  );

  constructor() {
    effect(() => {
      const hintMessage = this.hintMessage();
      if (hintMessage) {
        untracked(() => hintMessage.start());
      }
    });
  }

  async ngOnInit(): Promise<void> {
    await this.tournamentService.refreshCurrentTournament();

    this.tournamentService.currentTournamentData$
      .pipe(
        startWith(this.tournamentService.currentTournamentSnapshot),
        switchMap((currentTournamentData) => {
          this.currentTournamentData.set(currentTournamentData);
          if (!this.themeService.hasWaitingOpponents()) {
            return of(undefined);
          }
          return from(
            this.waitingOpponentsService.fetchWaitingOpponents(
              this.gameId(),
              currentTournamentData?.tournamentId,
            ),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((waitingOpponents) => {
        this.waitingOpponentsStandard.set(waitingOpponents);
      });

    this.operatorService.updateBalance(OperatorService.IsDemo);

    this.checkUnfinishedRoom();

    if (this.autoOpenWagerDrawer()) {
      await this.openWagerModal();
    }

    if (this.autoOpenSoloSheet()) {
      await this.openSoloSheet();
    }
  }

  ngAfterViewInit(): void {
    this.initialised$
      .pipe(
        filter((initialised) => initialised),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(async () => {
        if (!this.roomsResultsService.hasUnfinishedRoom()) {
          return;
        }

        const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
        const continueGameModal = await this.modalController.create({
          component: ContinueGameDrawerComponent,
          ...getModalConfig(asSheetModal, { mobile: 'custom-modal-height' }),
        });
        await continueGameModal.present();

        this.analyticsService.log(EventName.ContinueGamePopupShow);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public fetchHints: HintMessageFetcher = (request) => {
    return this.activityFeedService.fetchHints(request.isInitial);
  };

  async playButtonClicked(): Promise<void> {
    if (this.hasUnfinishedRoom()) {
      const gameContinued = await this.roomsResultsService.launchUnfinishedGame();
      if (gameContinued) {
        this.analyticsService.log(EventName.ContinueGameScreenLaunchGame);
        return;
      }
    }
    await this.openWagerModal();
  }

  async openWagerModal(): Promise<void> {
    this.analyticsService.log(EventName.ChangePrizeButtonClicked);

    const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);

    const wagerModal = await this.modalController.create({
      ...modalContent(WagerDrawerComponent, {
        gameId: this.gameId(),
        allTiers: this.allTiers,
        freeMatches: this.freeMatches,
        currentTournamentData: this.currentTournamentData,
        soloTiers: this.soloTiersService.soloTiers,
        waitingOpponentsStandard: this.waitingOpponentsStandard,
      }),
      ...getModalConfig(asSheetModal, { mobile: 'custom-modal-height' }),
    });

    await wagerModal.present();
    // Ionic's onDidDismiss only hears a dismissal that happens after the call, and a fast tap can
    // dismiss the drawer while the refreshes below are still out, so subscribe before them.
    const dismissed = wagerModal.onDidDismiss();

    // Grants and Solo tiers can change behind the player's back; the drawer is where they look, so
    // refresh them on open. A refetch failure must not block the drawer. Grants keep their stale
    // tiles; a failed Solo refresh never rejects but empties its list until the next open.
    await Promise.all([
      this.tournamentService.refreshCurrentTournament(),
      this.roomsResultsService.refreshResults(),
      this.tiersService.refreshFreeMatches().catch(() => undefined),
      this.soloTiersService.refresh(),
    ]);

    const { data, role } = await dismissed;
    // A state change can replace these controls while the drawer is open. Nobody is listening for
    // the choice any more, and emitting on a destroyed output logs NG0953.
    if (this.destroyRef.destroyed) {
      return;
    }
    if (role === 'practiceClick') {
      this.analyticsService.log(EventName.PracticeButtonClicked);
      this.launchPracticeEvent.emit();
    } else if (role === 'localClick') {
      this.launchLocalEvent.emit();
    } else if (role === 'wagerClick') {
      // The dismissal data is the duel event object, so the tier id sits under tierData.
      this.analyticsService.log(EventName.PlayButtonClicked, { tierId: data.tierData.tierId });
      this.showPaymentEvent.emit(data);
    } else if (role === 'tournamentPlayClick') {
      this.analyticsService.log(EventName.TournamentPlayButtonClicked);
      this.showPaymentEvent.emit(data);
    } else if (role === 'freeMatchClick') {
      // No log here: FreeMatchPlayButtonClicked fires in PaymentService where the play is
      // dispatched, once for both the drawer and tier-first surfaces, with grantId and remaining.
      this.showPaymentEvent.emit(data);
    } else if (role === 'soloClick') {
      await this.openSoloSheet();
    }
  }

  // The sheet pays for itself, so nothing is emitted: the game launches from inside it.
  async openSoloSheet(): Promise<void> {
    await this.soloSheetService.open(this.gameId());
  }

  private async checkUnfinishedRoom(): Promise<void> {
    const modalPresent = await this.modalController.getTop();

    if (!modalPresent) {
      this.initialised$
        .pipe(
          filter((initialised) => initialised),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe(async () => {
          try {
            // Before showing the tutorial, make sure there isn't an unfinished room
            if (!this.roomsResultsService.hasUnfinishedRoom()) {
              await this.gameTutorialService.tryShowTutorial(this.gameId());
            }
          } catch (error) {
            this.analyticsService.handleError(error, 'checkUnfinishedRoom');
          }
        });
    }
  }

  private selectedTierData(index: number): DuelTierDataWithMeta | undefined {
    if (this.allTiers().length > 0 && index >= 0 && index < this.allTiers().length) {
      return this.allTiers()[index];
    }
    return undefined;
  }

  private getTranslation(key: string, params?: Record<string, unknown>): string {
    return this.translocoService.translate(`components.loading-game-controls.${key}`, params);
  }
}
